import { LuIcon } from "@overmap-ai/blocks"
import { type DateField, fieldDataMapping } from "@overmap-ai/forms"
import { use } from "react"

import { DayPickerPopover } from "../../../components/Popovers/DayPickerPopover"
import { formatCompactDate, formatDateRange } from "../../../utils/date"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { DateFieldFilterDef } from "./DateFieldFilterDef"

interface DateFieldFilterCellProps extends FilterDefRenderProps<DateFieldFilterDef> {
	field: DateField
}

export const DateFieldFilterCell = (props: DateFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<DayPickerPopover
						required={false}
						size={size}
						mode="single"
						selected={equalsFilterValue ?? undefined}
						onSelect={(date) => filter.setFilterValue(date ?? null)}
					>
						<FilterCellButton className="truncate">
							{equalsFilterValue !== undefined
								? equalsFilterValue
									? formatCompactDate(equalsFilterValue)
									: "empty"
								: "..."}
						</FilterCellButton>
					</DayPickerPopover>
				)
			}
			case "before":
			case "after": {
				const afterModifier = filterDef.modifiers.after
				const afterFilterValue =
					filterValue !== undefined && afterModifier.isFilterValueValid(filterValue) ? filterValue : undefined

				return (
					<DayPickerPopover
						required
						size={size}
						mode="single"
						selected={afterFilterValue}
						onSelect={filter.setFilterValue}
					>
						<FilterCellButton className="truncate">
							{afterFilterValue ? formatCompactDate(afterFilterValue) : "..."}
						</FilterCellButton>
					</DayPickerPopover>
				)
			}
			case "in":
			case "notIn": {
				const inModifier = filterDef.modifiers.in
				const inFilterValue =
					filterValue !== undefined && inModifier.isFilterValueValid(filterValue) ? filterValue : undefined

				return (
					<DayPickerPopover
						required
						mode="range"
						size={size}
						selected={inFilterValue}
						onSelect={filter.setFilterValue}
					>
						<FilterCellButton className="truncate">
							{inFilterValue ? formatDateRange(inFilterValue) : "..."}
						</FilterCellButton>
					</DayPickerPopover>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
